import { memo, useCallback, useMemo } from "react";
import { useT } from "@transifex/react";
import type { SchoolClass } from "p6m-p6u";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import SearchableFilter, { SEARCHABLE_FILTER_ALL_VALUE } from "../../basic/searchableFilter/SearchableFilter";
import SortSelect from "../../basic/sortSelect/SortSelect";
import FilterBarLayout from "../../basic/filterBarLayout/FilterBarLayout";

export enum SortByOptions {
    CREATED_DESC = "CREATED_DESC",
    CREATED_ASC = "CREATED_ASC",
}

interface ActivityFilterBarProps {
    classFilter: string;
    onClassFilterChange: (value: string) => void;
    availableClasses: SchoolClass[];
    languageFilter: string;
    onLanguageFilterChange: (value: string) => void;
    sortBy: SortByOptions;
    onSortByChange: (value: SortByOptions) => void;
    availableLanguages: string[];
}

export const ClassActivityFilterBar = memo((props: ActivityFilterBarProps) => {
    const t = useT();
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const t_class = t("Class", { _tags: "AssistantOverview" });
    const t_language = t("Language", { _tags: "AssistantOverview" });
    const t_all = t("All", { _tags: "AssistantOverview" });
    const t_modifiedDesc = t("Modification date (descending)", { _tags: "AssistantOverview" });
    const t_modifiedAsc = t("Modification date (ascending)", { _tags: "AssistantOverview" });

    const classOptions = useMemo(() => {
        const items = props.availableClasses.map((cls) => ({ name: cls.name, value: cls.token }));
        return items.length > 0 ? [{ name: t_all, value: SEARCHABLE_FILTER_ALL_VALUE }, ...items] : [];
    }, [props.availableClasses, t_all]);

    const languageOptions = useMemo(() => {
        const items = props.availableLanguages.map((lang) => ({ name: lang, value: lang }));
        return items.length > 0 ? [{ name: t_all, value: SEARCHABLE_FILTER_ALL_VALUE }, ...items] : [];
    }, [props.availableLanguages, t_all]);

    const sortOptions = [
        { name: t_modifiedDesc, value: SortByOptions.CREATED_DESC },
        { name: t_modifiedAsc, value: SortByOptions.CREATED_ASC },
    ];

    const onSortByChange = props.onSortByChange;
    const handleSortByChange = useCallback((val: string) => onSortByChange(val as SortByOptions), [onSortByChange]);

    return (
        <FilterBarLayout
            filterContent={(filterLabelId) => (
                <>
                    <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "min-w-[200px]", "w-full")}>
                        <SearchableFilter
                            options={classOptions}
                            value={props.classFilter}
                            onChange={props.onClassFilterChange}
                            placeholder={t_class}
                            aria-labelledby={filterLabelId}
                        />
                    </div>

                    <div className={getStyleBasedOnBreakpoint(Breakpoint.MD, "min-w-[200px]", "w-full")}>
                        <SearchableFilter
                            options={languageOptions}
                            value={props.languageFilter}
                            onChange={props.onLanguageFilterChange}
                            placeholder={t_language}
                            aria-labelledby={filterLabelId}
                        />
                    </div>
                </>
            )}
            sortContent={(sortLabelId) => (
                <SortSelect
                    options={sortOptions}
                    value={props.sortBy}
                    onChange={handleSortByChange}
                    aria-labelledby={sortLabelId}
                />
            )}
        />
    );
});
